import {Suspense, useMemo, useState, lazy} from 'react';
import {
  useAnamnesisQuestions,
  useBodySites,
  useScoringSystems
} from '@legit.health/backend-client-elements';
import {AnonymousDiagnosticReport, BodySiteCode, ImageValue, indexArrayBy} from '@legit.health/ui';
import {useTranslation} from 'react-i18next';
import AppSkeleton from '@/components/AppSkeleton';
import {useParamsContext} from '@/contexts/ParamsContext';
import useLocale from '@/hooks/useLocale';
import ImageSent from './components/ImageSent/ImageSent';
import InitialForm from './components/InitialForm';
import {QuestionnairesForm, QuestionnairesSkeleton} from './components/QuestionnairesForm';
const DiagnosticReport = lazy(() => import('@/components/DiagnosticReport'));

export default function DiagnosticReportForm() {
  const {enableAnamnesis, enableResult} = useParamsContext();
  const {t} = useTranslation(['app', 'ui']);
  const locale = useLocale();
  const {
    data: {data: anamnesisQuestions}
  } = useAnamnesisQuestions({locale});
  if (!anamnesisQuestions && enableAnamnesis) {
    throw new Error('Cannot load anamnesis questions');
  }

  const [initialFormResponse, setInitialFormResponse] = useState<{
    diagnosticReport: AnonymousDiagnosticReport;
    images: ImageValue[];
    bodySiteCode: BodySiteCode;
    visitIdentifier: string;
    anamnesisQuestions: Record<string, string>;
  } | null>(null);
  const [diagnosedDiagnosticReport, setDiagnosedDiagnosticReport] =
    useState<AnonymousDiagnosticReport | null>(null);

  const {
    data: {data: bodySites}
  } = useBodySites({locale});
  const {
    data: {data: scoringSystems}
  } = useScoringSystems({locale});
  const scoringSystemsById = useMemo(() => indexArrayBy(scoringSystems, 'id'), [scoringSystems]);

  if (!initialFormResponse) {
    return (
      <InitialForm
        bodySites={bodySites}
        t={t}
        locale={locale}
        onSuccessfullResponse={(r) => setInitialFormResponse(r)}
        anamnesisQuestions={anamnesisQuestions ?? []}
      />
    );
  }

  const firstConclusion = initialFormResponse.diagnosticReport.result.conclusions[0];

  if (
    !initialFormResponse.diagnosticReport.result.admitsSeverityAssessment ||
    diagnosedDiagnosticReport
  ) {
    if (enableResult) {
      return (
        <Suspense fallback={<AppSkeleton />}>
          <DiagnosticReport
            diagnosticReport={diagnosedDiagnosticReport ?? initialFormResponse.diagnosticReport}
          />
        </Suspense>
      );
    } else {
      return (
        <ImageSent
          t={t}
          diagnosticReport={diagnosedDiagnosticReport ?? initialFormResponse.diagnosticReport}
        />
      );
    }
  }

  const bodySite = bodySites.find((b) => b.code === initialFormResponse.bodySiteCode);

  if (!bodySite) {
    throw new Error();
  }

  return (
    <Suspense fallback={<QuestionnairesSkeleton />}>
      <QuestionnairesForm
        pathology={firstConclusion.pathology}
        diagnosisSupportId={initialFormResponse.diagnosticReport.id}
        bodySite={bodySite}
        onReceiveDiagnosticReportResult={(dr) => setDiagnosedDiagnosticReport(dr)}
        scoringSystemsById={scoringSystemsById}
      />
    </Suspense>
  );
}
